import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { registerUser, getStores, getUsers, updateUser, toggleUserActive, deleteUser } from '../api/api';
import { UserPlus, Users as UsersIcon, Edit2, UserX, UserCheck, Trash2, Eye, EyeOff } from 'lucide-react';

const Users = () => {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    name: '',
    role: 'cashier',
    store_id: ''
  });
  const [editData, setEditData] = useState({
    email: '',
    name: '',
    role: '',
    store_id: '',
    password: '',
    max_devices: 3
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState({});  // Controlar visibilidad de contraseñas

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const loadData = useCallback(async () => {
    try {
      const [storesData, usersData] = await Promise.all([getStores(), getUsers()]);
      setStores(storesData);
      setUsers(usersData);
      if (storesData.length > 0) {
        setFormData(prev => ({ ...prev, store_id: storesData[0].id }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const userData = { ...formData };
      if (formData.role === 'admin' || formData.role === 'manager' || formData.role === 'supervisor' || formData.role === 'admin_assistant') {
        userData.store_id = null;
      }
      
      await registerUser(userData);
      setSuccess('¡Usuario creado exitosamente!');
      setFormData({
        username: '',
        password: '',
        email: '',
        name: '',
        role: 'cashier',
        store_id: stores[0]?.id || ''
      });
      setShowCreateForm(false);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear el usuario');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditData({
      email: userToEdit.email,
      name: userToEdit.name,
      role: userToEdit.role,
      store_id: userToEdit.store_id || '',
      password: '',
      max_devices: userToEdit.max_devices || 3
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const userData = { ...editData };
      if (!userData.password) {
        delete userData.password;
      }
      if (editData.role === 'admin' || editData.role === 'manager' || editData.role === 'supervisor' || editData.role === 'admin_assistant') {
        userData.store_id = null;
      }
      
      await updateUser(selectedUser.id, userData);
      setSuccess('¡Usuario actualizado exitosamente!');
      setShowEditModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al actualizar el usuario');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    const action = currentStatus ? 'desactivar' : 'activar';
    if (!window.confirm(`¿Está seguro de ${action} este usuario?`)) return;

    try {
      await toggleUserActive(userId);
      setSuccess(`¡Usuario ${action === 'desactivar' ? 'desactivado' : 'activado'} exitosamente!`);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || `Error al ${action} el usuario`);
    }
  };

  const roles = [
    { value: 'admin', label: 'Administrador', needsStore: false },
    { value: 'manager', label: 'Gerencia', needsStore: false },
    { value: 'supervisor', label: 'Supervisor', needsStore: false },
    { value: 'admin_assistant', label: 'Auxiliar Administrativo', needsStore: false },
    { value: 'store_manager', label: 'Encargado de Tienda', needsStore: true },
    { value: 'purchase_manager', label: 'Encargado de Compras', needsStore: false },
    { value: 'cashier', label: 'Cajero/Usuario de Tienda', needsStore: true }
  ];

  const selectedRole = roles.find(r => r.value === formData.role);
  const selectedEditRole = roles.find(r => r.value === editData.role);

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      manager: 'bg-blue-100 text-blue-800',
      supervisor: 'bg-cyan-100 text-blue-800',
      admin_assistant: 'bg-pink-100 text-pink-800',
      store_manager: 'bg-green-100 text-green-800',
      purchase_manager: 'bg-orange-100 text-orange-800',
      cashier: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || colors.cashier;
  };

  const getRoleLabel = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`¿Estás seguro de eliminar permanentemente al usuario "${username}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await deleteUser(userId);
      setSuccess('Usuario eliminado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar usuario');
      setTimeout(() => setError(''), 5000);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
        <p className="text-red-800 text-lg">No tienes permisos para acceder a esta sección</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header futurista */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent flex items-center space-x-3" data-testid="users-title">
            <UsersIcon size={40} className="text-indigo-600" />
            <span>Gestión de Usuarios</span>
          </h1>
          <p className="text-gray-600 mt-2 ml-1">Crear y administrar usuarios del sistema</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-6 py-3 bg-gradient-to-r from-slate-800 to-blue-700 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center space-x-2 font-semibold"
          data-testid="add-user-button"
        >
          <UserPlus size={22} />
          <span>Nuevo Usuario</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center space-x-3" data-testid="users-error">
          <span className="text-2xl">⚠️</span>
          <span className="font-medium">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-xl flex items-center space-x-3 animate-pulse" data-testid="users-success">
          <span className="text-2xl">✅</span>
          <span className="font-medium">{success}</span>
        </div>
      )}

      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Crear Nuevo Usuario</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nombre completo"
                  required
                  data-testid="name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Usuario
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nombre de usuario"
                  required
                  data-testid="username-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@ejemplo.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Contraseña"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rol
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="role-select"
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>

              {selectedRole?.needsStore && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tienda
                  </label>
                  <select
                    value={formData.store_id}
                    onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    data-testid="store-select"
                  >
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                data-testid="submit-user-button"
              >
                {submitting ? 'Creando...' : 'Crear Usuario'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Usuarios */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Usuarios del Sistema</h2>
        </div>
        
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Cargando usuarios...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <div className="bg-gray-100 rounded-full w-24 h-24 mx-auto flex items-center justify-center mb-4">
              <UsersIcon className="text-gray-400" size={48} />
            </div>
            <p className="text-gray-600 font-medium">No hay usuarios registrados</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-slate-800 to-blue-700 text-white">
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide rounded-tl-xl">Nombre</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Usuario</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Contraseña</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Rol</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Tienda</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Disp. Máx</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Estado</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide rounded-tr-xl">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((userItem, index) => (
                  <tr key={userItem.id} className={`hover:bg-indigo-50/50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {userItem.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {userItem.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                          {visiblePasswords[userItem.id] 
                            ? (userItem.password_plain || '••••••') 
                            : '••••••'}
                        </span>
                        <button
                          onClick={() => togglePasswordVisibility(userItem.id)}
                          className="text-gray-500 hover:text-gray-700"
                          title={visiblePasswords[userItem.id] ? 'Ocultar' : 'Mostrar'}
                        >
                          {visiblePasswords[userItem.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {userItem.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(userItem.role)}`}>
                        {getRoleLabel(userItem.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {userItem.store_id ? (
                        stores.find(s => s.id === userItem.store_id)?.name || 'N/A'
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                        {userItem.max_devices || 3}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {userItem.is_active ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Activo
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                      <button
                        onClick={() => handleEdit(userItem)}
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center space-x-1"
                      >
                        <Edit2 size={16} />
                        <span>Editar</span>
                      </button>
                      <button
                        onClick={() => handleToggleActive(userItem.id, userItem.is_active)}
                        className={`${userItem.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'} inline-flex items-center space-x-1`}
                      >
                        {userItem.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                        <span>{userItem.is_active ? 'Desactivar' : 'Activar'}</span>
                      </button>
                      {!userItem.is_active && (
                        <button
                          onClick={() => handleDeleteUser(userItem.id, userItem.username)}
                          className="text-red-700 hover:text-red-900 inline-flex items-center space-x-1 ml-2"
                          title="Eliminar permanentemente"
                        >
                          <Trash2 size={16} />
                          <span>Eliminar</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Edición */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Editar Usuario: {selectedUser.username}</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rol
                  </label>
                  <select
                    value={editData.role}
                    onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                {selectedEditRole?.needsStore && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tienda
                    </label>
                    <select
                      value={editData.store_id}
                      onChange={(e) => setEditData({ ...editData, store_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nueva Contraseña (dejar vacío para mantener la actual)
                  </label>
                  <input
                    type="password"
                    value={editData.password}
                    onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Nueva contraseña (opcional)"
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Máximo de Dispositivos
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={editData.max_devices}
                    onChange={(e) => setEditData({ ...editData, max_devices: parseInt(e.target.value) || 3 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cantidad de dispositivos que este usuario puede tener autorizados simultáneamente
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {submitting ? 'Guardando...' : 'Guardar Cambios'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Roles del Sistema</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(role => (
            <div key={role.value} className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">{role.label}</h3>
              <p className="text-sm text-gray-600">
                {role.value === 'admin' && 'Acceso completo al sistema, puede crear usuarios y tiendas'}
                {role.value === 'manager' && 'Puede ver todas las tiendas y generar reportes'}
                {role.value === 'supervisor' && 'Supervisa todas las tiendas, acceso de lectura'}
                {role.value === 'admin_assistant' && 'Auxiliar administrativo, acceso configurable según permisos asignados'}
                {role.value === 'store_manager' && 'Administra su tienda, genera reportes y códigos de autorización'}
                {role.value === 'purchase_manager' && 'Encargado de compras, ve reporte de proveedores'}
                {role.value === 'cashier' && 'Registra ventas y gastos en su tienda'}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Users;